import React from 'react';

// Renders per-route head metadata. Relies on React 19 native head hoisting:
// <title>, <meta>, and <link rel> elements rendered anywhere in the tree are
// hoisted to <head> automatically. No provider, no dependency.
//
// This component is additive: if omitted from a page, the defaults in
// public/index.html continue to apply. Never throws; safe on any route.

const SITE_URL = 'https://lotushealingspa.com';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.jpg`;

type Props = {
  title: string;
  description: string;
  path: string;
  image?: string;
  noindex?: boolean;
  /** Blog posts are `article`; everything else stays on the default. */
  type?: 'website' | 'article';
  /** ISO date. Only emitted on `article`, where Open Graph defines it. */
  publishedTime?: string;
  /** ISO date. Only emitted on `article`. */
  modifiedTime?: string;
};

const SEO: React.FC<Props> = ({
  title,
  description,
  path,
  image,
  noindex,
  type = 'website',
  publishedTime,
  modifiedTime,
}) => {
  const canonical = `${SITE_URL}${path}`;
  const ogImage = image || DEFAULT_OG_IMAGE;
  const isArticle = type === 'article';

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Healing Lotus Skin Spa" />
      {isArticle && publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {isArticle && modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
    </>
  );
};

export default SEO;
